import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, IconButton, InputAdornment, Link, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Clear, CloudUploadOutlined, Close, Download, Search, TableChartOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { apiService } from '../../services/api';
import { examService } from '../../services/examService';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { useSessionContext } from '../../context/SessionContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { useAppSelector } from '../../store/hooks';
import { getGradeColorFromPercentage, getGradeFromPercentage } from '../../utils/gradeUtils';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { fmtClassName, usePdfExport, useSchoolBranding } from '../../utils/pdf';
import TranscriptDocument from '../../components/examination/pdf/TranscriptDocument';
import BusyIcon from '../../components/common/BusyIcon';
import PdfRenderingDialog from '../../components/common/PdfRenderingDialog';
import type { TranscriptPrint } from '../../types/examination';
import type { SectionInstance, Session, SessionClass } from '../../types/sessions';
import FilterBar from '../../components/common/FilterBar';
import SearchableSelect from '../../components/common/SearchableSelect';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import PersonCell from '../../components/people/PersonCell';
import { tableHeadCellSx } from '../../components/admissions/tabLayout';
import BulkResultUpload from '../../components/BulkResultUpload';
import StudentResultsDialog, { StudentResultsTarget } from '../../components/examination/results/StudentResultsDialog';

/** One enrollment (a student's year in a class) with their averages over the exams that count in reports. */
interface StudentResultRow {
  student: { _id: string; name: string; rollNumber: number };
  enrollment: { _id: string; sessionId: string; classId: string; sectionInstanceId: string | null };
  class: { _id: string; name: string } | null;
  section: { _id: string; name: string } | null;
  session: { _id: string; name: string; status?: string } | null;
  totalSubjects: number;
  totalExams: number;
  averagePercentage: number | null;
  lastUpdated: string;
  canEdit: boolean;
}

/** Most transcripts in one PDF — the server refuses more. */
const BULK_TRANSCRIPT_CAP = 200;

/**
 * Results — each student's exams for the session, their average over the
 * exams that count in reports, and transcripts (one, or up to 200 in a PDF).
 * Opening a student shows every paper; their class teacher can correct
 * marks there under the same rules as Enter Marks.
 */
const ResultsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const { activeSession } = useSessionContext();
  const grades = useAppSelector(state => state.grades.grades);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [sections, setSections] = useState<SectionInstance[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 300);
  const [grInput, setGrInput] = useState('');
  const grNumber = useDebounce(grInput, 300);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [rows, setRows] = useState<StudentResultRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [open, setOpen] = useState<StudentResultsTarget | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  useEffect(() => {
    Promise.all([sessionsApi.list(), classesApi.list()])
      .then(([s, c]) => { setSessions(s); setClasses(c); })
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load the filters', { variant: 'error' }));
  }, [enqueueSnackbar]);
  useEffect(() => { if (!sessionId && activeSession) setSessionId(activeSession._id); }, [activeSession, sessionId]);

  useEffect(() => {
    if (!sessionId || !classId) { setSections([]); return; }
    let cancelled = false;
    sectionInstancesApi.list({ sessionId, classId })
      .then(s => { if (!cancelled) setSections(s); })
      .catch((err: any) => { if (!cancelled) enqueueSnackbar(err?.message || 'Could not load the sections', { variant: 'error' }); });
    return () => { cancelled = true; };
  }, [sessionId, classId, enqueueSnackbar]);

  const filter = useMemo(() => ({
    sessionId,
    classId: classId || undefined,
    sectionInstanceId: classId && sectionId ? sectionId : undefined,
    search: search.trim() || undefined,
    grNumber: grNumber || undefined,
  }), [sessionId, classId, sectionId, search, grNumber]);

  // A new filter is a new list: first page, and a "whole filter" selection no longer applies.
  useEffect(() => { setPage(0); setAllMatching(false); }, [filter]);

  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!sessionId) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await apiService.get<{ students: StudentResultRow[]; pagination: { total: number } }>('/examination/student-results', { ...filter, page: page + 1, limit: rowsPerPage });
      if (mine !== seq.current) return;
      setRows(res.data?.students ?? []);
      setTotal(res.data?.pagination.total ?? 0);
    } catch (err: any) {
      if (mine === seq.current) enqueueSnackbar(err?.message || 'Could not load the results', { variant: 'error' });
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [sessionId, filter, page, rowsPerPage, enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const sessionOptions = useMemo(() => buildSessionOptions(sessions, theme), [sessions, theme]);
  const classOptions = useMemo(() => classes.map(c => ({ value: c._id, label: fmtClassName(c.name) })), [classes]);
  const sectionOptions = useMemo(() => sections.map(s => ({ value: s._id, label: s.name })), [sections]);
  const gradeColor = (pct: number | null) => (pct == null ? theme.palette.text.disabled : getGradeColorFromPercentage(pct, grades));

  // ── Selection for bulk transcripts ──────────────────────────────────────
  const pageIds = rows.map(r => r.enrollment._id);
  const pageTicked = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const pageSome = pageIds.some(id => selected.has(id));
  const toggle = (id: string) => {
    setAllMatching(false);
    setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const togglePage = () => {
    setAllMatching(false);
    setSelected(prev => { const next = new Set(prev); pageIds.forEach(id => (pageTicked ? next.delete(id) : next.add(id))); return next; });
  };
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };

  const selectAllMatching = async () => {
    try {
      const res = await apiService.get<{ students: StudentResultRow[] }>('/examination/student-results', { ...filter, page: 1, limit: BULK_TRANSCRIPT_CAP });
      setSelected(new Set((res.data?.students ?? []).map(r => r.enrollment._id)));
      setAllMatching(true);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not select them all', { variant: 'error' });
    }
  };

  // ── Transcripts, drawn in the browser from what the server worked out ──
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const transcriptDoc = (transcripts: TranscriptPrint[]) => (
    <TranscriptDocument transcripts={transcripts} branding={branding} generatedAt={new Date().toISOString()} />
  );

  // How many are being printed — the selection clears on success, while the dialog is still fading out.
  const [printing, setPrinting] = useState(0);
  const downloadSelected = async () => {
    const ids = Array.from(selected);
    setPrinting(ids.length);
    setBulkBusy(true);
    try {
      const done = await generatePdf(
        async () => transcriptDoc(await examService.printTranscripts(ids)),
        `transcripts-${ids.length}-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      );
      if (done) clearSelection();
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadOne = async (row: StudentResultRow) => {
    setDownloading(row.enrollment._id);
    try {
      await generatePdf(
        async () => transcriptDoc([await examService.getTranscript(row.student._id, row.enrollment.classId, row.enrollment.sessionId)]),
        `transcript-${row.student.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.pdf`,
      );
    } finally {
      setDownloading(null);
    }
  };

  const filtered = !!(classId || search.trim() || grNumber);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Results</Typography>
          <Typography variant="body2" color="text.secondary">Each student's exams this session, their average, and transcripts.</Typography>
        </Box>
        {canWrite('results') && (
          <Button variant="outlined" startIcon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>Upload marks</Button>
        )}
      </Stack>

      <FilterBar loading={loading && rows.length > 0}>
        <TextField
          size="small"
          placeholder="Search by name or roll"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
            endAdornment: searchInput ? <InputAdornment position="end"><IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined,
          }}
        />
        <TextField
          size="small"
          label="GR No."
          value={grInput}
          onChange={e => setGrInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          sx={{ width: 120 }}
        />
        <SearchableSelect label="Session" value={sessionId} onChange={v => { setSessionId(v); setClassId(''); setSectionId(''); }} options={sessionOptions} clearable={false} minWidth={200} />
        <SearchableSelect label="Class" placeholder="All classes" value={classId} onChange={v => { setClassId(v); setSectionId(''); }} options={classOptions} minWidth={150} />
        <SearchableSelect
          label="Section"
          placeholder={classId ? 'All sections' : 'Pick a class first'}
          value={sectionId}
          onChange={setSectionId}
          options={sectionOptions}
          disabled={!classId || sectionOptions.length === 0}
          minWidth={170}
        />
      </FilterBar>

      <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {selected.size > 0 && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.05), borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
              {allMatching ? `All ${selected.size} matching students selected` : `${selected.size} selected`}
              {!allMatching && pageTicked && total > rows.length && (
                total > BULK_TRANSCRIPT_CAP
                  ? <Typography component="span" variant="body2" color="text.secondary"> · narrow the filter to select all {total} (at most {BULK_TRANSCRIPT_CAP} per PDF)</Typography>
                  : <> · <Link component="button" type="button" onClick={selectAllMatching} underline="hover" sx={{ fontWeight: 700, verticalAlign: 'baseline' }}>Select all {total}</Link></>
              )}
            </Typography>
            <Button size="small" color="inherit" startIcon={<Close fontSize="small" />} onClick={clearSelection} disabled={bulkBusy}>Clear</Button>
            <Button
              size="small"
              variant="contained"
              disableElevation
              onClick={downloadSelected}
              disabled={bulkBusy || selected.size > BULK_TRANSCRIPT_CAP}
              startIcon={<BusyIcon busy={bulkBusy} size={14}><Download fontSize="small" /></BusyIcon>}
            >
              {bulkBusy ? 'Preparing…' : `Transcripts (${selected.size})`}
            </Button>
          </Stack>
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': tableHeadCellSx }}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={pageTicked} indeterminate={!pageTicked && pageSome} onChange={togglePage} disabled={!rows.length} inputProps={{ 'aria-label': 'Select every student on this page' }} />
                </TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Class</TableCell>
                <TableCell align="right">Exams</TableCell>
                <TableCell>Average</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right" width={150} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRowSkeletons columns={7} rows={8} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6 }}>
                    <EmptyState
                      icon={<TableChartOutlined />}
                      title={filtered ? 'No students match' : 'No students yet'}
                      description={filtered ? 'Try another class, name or GR number.' : 'Students enrolled in this session appear here with their exams.'}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.map(row => {
                const ticked = selected.has(row.enrollment._id);
                const pct = row.averagePercentage;
                return (
                  <TableRow key={row.enrollment._id} hover selected={ticked} onClick={() => setOpen({ studentId: row.student._id, studentName: row.student.name, classId: row.enrollment.classId, sessionId: row.enrollment.sessionId })} sx={{ cursor: 'pointer' }}>
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Checkbox size="small" checked={ticked} onChange={() => toggle(row.enrollment._id)} inputProps={{ 'aria-label': `Select ${row.student.name}` }} />
                    </TableCell>
                    <TableCell><PersonCell name={row.student.name} secondary={`Roll ${row.student.rollNumber ?? '—'}`} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{row.class ? fmtClassName(row.class.name) : '—'}</Typography>
                      {row.section && <Typography variant="caption" color="text.secondary">Section {row.section.name}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.totalExams}</TableCell>
                    <TableCell>
                      {pct == null ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 48 }}>{pct.toFixed(1)}%</Typography>
                          <Chip label={getGradeFromPercentage(pct, grades)} size="small" sx={{ bgcolor: gradeColor(pct), color: 'common.white', fontWeight: 700, minWidth: 34, height: 22 }} />
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{row.lastUpdated ? format(new Date(row.lastUpdated), 'd MMM yyyy') : '—'}</Typography></TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Download transcript">
                        <span>
                          <IconButton size="small" onClick={() => downloadOne(row)} disabled={downloading === row.enrollment._id} aria-label={`Transcript for ${row.student.name}`}>
                            <BusyIcon busy={downloading === row.enrollment._id}><Download fontSize="small" /></BusyIcon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={() => setOpen({ studentId: row.student._id, studentName: row.student.name, classId: row.enrollment.classId, sessionId: row.enrollment.sessionId })}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <StudentResultsDialog target={open} onClose={() => setOpen(null)} onChanged={load} />
      <PdfRenderingDialog open={bulkBusy} label={`Preparing ${printing} transcript${printing === 1 ? '' : 's'}`} hint="Large batches take a few seconds." />
      <BulkResultUpload open={uploadOpen} onClose={() => setUploadOpen(false)} onSuccess={load} />
    </Box>
  );
};

export default ResultsPage;
